import { Dispatch, SetStateAction } from 'react';
import * as workoutAnalytics from '../analytics/workout-analytics';
import { ESet } from '../models/ESet';
import { ActiveWorkout } from '../models/Workout';
import { addSetToExercise, removeSetFromExercise, updateSetInExercise } from '../state/active-workout';

type SetActiveWorkout = Dispatch<SetStateAction<ActiveWorkout | undefined>>;

/**
 * Single place responsible for every mutation to a set on the active workout.
 * All updates are applied as a patch merged onto the existing set, so callers
 * only ever need to describe what changed (reps, weight, done, feedback, ...)
 * instead of reconstructing the whole set - which is what let unrelated
 * fields (e.g. bodyweight) get silently clobbered.
 */
export function useSetMutations(activeWorkout: ActiveWorkout | undefined, setActiveWorkout: SetActiveWorkout) {
  const addSet = (exerciseName: string, set: ESet) => {
    setActiveWorkout(existingWorkout => addSetToExercise(existingWorkout!, exerciseName, set));
  };

  const removeSet = (exerciseName: string, index: number) => {
    setActiveWorkout(existingWorkout => removeSetFromExercise(existingWorkout!, exerciseName, index));
  };

  const updateSet = (exerciseName: string, index: number, patch: Partial<ESet>) => {
    const existingSet = activeWorkout?.exercises.find(e => e.name === exerciseName)?.sets[index];
    if (existingSet && patch.done && !existingSet.done) {
      // marked done for the first time
      workoutAnalytics.setCompleted(activeWorkout!, { ...existingSet, ...patch });
    }
    setActiveWorkout(existingWorkout => updateSetInExercise(existingWorkout!, exerciseName, index, patch));
  };

  return { addSet, removeSet, updateSet };
}
